interface TranslationProgress {
  language: string
  status: 'pending' | 'translating' | 'completed' | 'error' | 'saving'
  startTime?: number
  endTime?: number
  error?: string
  translatedText?: string
}

interface BulkTranslationProgressModalProps {
  progress: Record<string, TranslationProgress>
  onClose: () => void
}

export function BulkTranslationProgressModal({ progress, onClose }: BulkTranslationProgressModalProps) {
  const progressEntries = Object.entries(progress)
  const completed = progressEntries.filter(([, p]) => p.status === 'completed').length
  const total = progressEntries.length
  const isTranslationInProgress = progressEntries.some(([, p]) => p.status === 'translating' || p.status === 'saving')

  // Group progress by challenge ID
  const groupedProgress = progressEntries.reduce((acc, [key, prog]) => {
    const [challengeId, language] = key.split('-')
    if (!acc[challengeId]) {
      acc[challengeId] = []
    }
    acc[challengeId].push({ key, lang: language, ...prog })
    return acc
  }, {} as Record<string, Array<{ key: string; lang: string } & TranslationProgress>>)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div 
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">
              Bulk Translation Progress ({completed}/{total})
            </h2>
            <button
              onClick={isTranslationInProgress ? undefined : onClose}
              disabled={isTranslationInProgress}
              className={`${
                isTranslationInProgress 
                  ? 'text-gray-300 cursor-not-allowed' 
                  : 'text-gray-400 hover:text-gray-600 cursor-pointer'
              }`}
              title={isTranslationInProgress ? 'Cannot close during translation' : 'Close'}
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Overall Progress */}
          <div className="bg-gray-50 rounded-full p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">Overall Progress</span>
              <span className="text-sm text-gray-600">{completed}/{total} completed</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="bg-primary-600 h-3 rounded-full transition-all duration-300"
                style={{ width: `${total > 0 ? (completed / total) * 100 : 0}%` }}
              />
            </div>
          </div>

          {/* Challenge Progress */}
          <div className="space-y-4">
            {Object.entries(groupedProgress).map(([challengeId, translations]) => {
              const challengeCompleted = translations.filter(t => t.status === 'completed').length
              const challengeTotal = translations.length

              return (
                <div key={challengeId} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <span className="font-medium text-gray-900">Challenge: {challengeId}</span>
                    <span className="text-sm text-gray-600">{challengeCompleted}/{challengeTotal}</span>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {translations.map((translation) => {
                      const duration = translation.startTime && translation.endTime 
                        ? ((translation.endTime - translation.startTime) / 1000).toFixed(1) 
                        : null

                      return (
                        <div key={translation.key} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div className="flex items-center space-x-2">
                            <div className={`w-2 h-2 rounded-full ${
                              translation.status === 'completed' ? 'bg-green-500' :
                              translation.status === 'translating' || translation.status === 'saving' ? 'bg-blue-500 animate-pulse' :
                              translation.status === 'error' ? 'bg-red-500' :
                              'bg-gray-300'
                            }`} />
                            <span className="text-sm font-medium">{translation.lang.toUpperCase()}</span>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-gray-600">
                              {translation.status === 'completed' && duration && `${duration}s`}
                              {translation.status === 'translating' && 'Translating...'}
                              {translation.status === 'saving' && 'Saving...'}
                              {translation.status === 'error' && 'Failed'}
                              {translation.status === 'pending' && 'Waiting...'}
                            </div>
                            {translation.error && (
                              <div className="text-xs text-red-500 max-w-20 truncate" title={translation.error}>
                                {translation.error}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="p-6 border-t border-gray-200">
          <button
            onClick={isTranslationInProgress ? undefined : onClose}
            disabled={isTranslationInProgress}
            className={`w-full px-4 py-2 rounded-full transition-colors ${
              isTranslationInProgress
                ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer'
            }`}
            title={isTranslationInProgress ? 'Translation must complete before closing' : ''}
          >
            {isTranslationInProgress ? 'Translation in Progress...' : completed === total ? 'Close' : 'Continue in Background'}
          </button>
        </div>
      </div>
    </div>
  )
} 